import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast, Toaster } from 'sonner';
import { CalendarDays, Clock, Car, User, Phone, CheckCircle, ChevronLeft, ChevronRight, Droplets, AlertCircle } from 'lucide-react';
import { hoyLocal, sumarDias } from '@/lib/fechas';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AgendaPublica() {
  const { slug } = useParams();
  const [negocio, setNegocio] = useState(null);
  const [error, setError] = useState('');
  const [paso, setPaso] = useState(1);
  const [fecha, setFecha] = useState('');
  const [slots, setSlots] = useState([]);
  const [mensajeSlots, setMensajeSlots] = useState('');
  const [horaSeleccionada, setHoraSeleccionada] = useState('');
  const [servicioSeleccionado, setServicioSeleccionado] = useState('');
  const [form, setForm] = useState({ cliente_nombre: '', cliente_telefono: '', placa: '', tipo_vehiculo: 'auto', notas: '' });
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [confirmado, setConfirmado] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/agenda/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setNegocio(d); setCargando(false); })
      .catch(() => { setError('Negocio no encontrado o agenda no disponible'); setCargando(false); });
  }, [slug]);

  // Default to today (usar timezone local, no UTC — evita desfase de 1 dia)
  useEffect(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    setFecha(`${yyyy}-${mm}-${dd}`);
  }, []);

  const cargarSlots = useCallback(async () => {
    if (!fecha) return;
    try {
      const res = await fetch(`${API}/api/agenda/${slug}/disponibilidad?fecha=${fecha}`);
      const data = await res.json();
      setSlots(data.slots || []);
      setMensajeSlots(data.mensaje || '');
    } catch { setSlots([]); setMensajeSlots(''); }
  }, [slug, fecha]);

  useEffect(() => { if (negocio) cargarSlots(); }, [negocio, cargarSlots]);

  // hoyStr en zona horaria estricta de México
  const hoyStr = hoyLocal();

  const cambiarDia = (dir) => {
    const newFecha = sumarDias(fecha, dir);
    if (newFecha < hoyStr) return;
    setFecha(newFecha);
    setHoraSeleccionada('');
  };

  const handleAgendar = async () => {
    const nombre = form.cliente_nombre.trim();
    const telSolo = (form.cliente_telefono || '').replace(/\D/g, '');
    if (!nombre || nombre.length < 2) { toast.error('Ingresa tu nombre completo'); return; }
    if (telSolo.length !== 10) { toast.error('El telefono debe tener exactamente 10 digitos'); return; }
    if (!form.placa.trim()) { toast.error('Ingresa la placa del vehiculo'); return; }
    setEnviando(true);
    try {
      const srv = negocio.servicios.find(s => s.id === servicioSeleccionado);
      const res = await fetch(`${API}/api/agenda/${slug}/agendar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fecha, hora: horaSeleccionada,
          servicio_id: servicioSeleccionado,
          servicio_nombre: srv?.nombre || '',
          ...form,
          cliente_nombre: form.cliente_nombre.trim(),
          cliente_telefono: form.cliente_telefono.replace(/\D/g, ''),
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      setConfirmado(true);
    } catch (e) { toast.error(e.message || 'Error al agendar'); }
    finally { setEnviando(false); }
  };

  const diasSemana = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
  const fechaObj = fecha ? new Date(fecha + 'T12:00:00') : new Date();

  if (cargando) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Card className="bg-slate-900 border-slate-800 max-w-sm w-full">
        <CardContent className="p-8 text-center">
          <Droplets className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (confirmado) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Toaster theme="dark" position="top-center" richColors />
      <Card className="bg-slate-900 border-slate-800 max-w-sm w-full">
        <CardContent className="p-8 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-xl font-bold text-slate-100">Cita Confirmada</h2>
          <p className="text-sm text-slate-400">{negocio?.mensaje_confirmacion || 'Tu cita ha sido agendada exitosamente.'}</p>
          <div className="bg-slate-800/50 rounded-sm p-3 text-left space-y-1">
            <p className="text-xs text-slate-500">Fecha: <span className="text-slate-200">{fecha}</span></p>
            <p className="text-xs text-slate-500">Hora: <span className="text-slate-200">{horaSeleccionada}</span></p>
            <p className="text-xs text-slate-500">Vehiculo: <span className="text-slate-200">{form.placa}</span></p>
          </div>
          <p className="text-xs text-slate-600 pt-2">{negocio.negocio} - {negocio.direccion}</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 py-6 px-4" data-testid="agenda-publica-page">
      <Toaster theme="dark" position="top-center" richColors />
      <div className="max-w-md mx-auto space-y-4">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-cyan-500/10 text-cyan-400 px-3 py-1 rounded-full text-xs font-medium mb-2">
            <Droplets className="w-3.5 h-3.5" /> Agenda Online
          </div>
          <h1 className="text-2xl font-bold text-slate-100 font-barlow">{negocio.negocio}</h1>
          {negocio.direccion && <p className="text-xs text-slate-500 mt-1">{negocio.direccion}</p>}
        </div>

        {/* Step 1: Date + Time */}
        {paso === 1 && (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4 space-y-4">
              <h3 className="text-sm font-bold text-slate-300 flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-cyan-400" /> Selecciona fecha y hora
              </h3>
              {/* Date navigator */}
              <div className="flex items-center justify-between bg-slate-800/50 rounded-sm px-3 py-2">
                <button onClick={() => cambiarDia(-1)}
                  disabled={fecha <= hoyStr}
                  className={`transition-colors ${fecha <= hoyStr ? 'text-slate-700 cursor-not-allowed' : 'text-slate-400 hover:text-slate-200'}`}
                  data-testid="fecha-prev-btn">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="text-center">
                  <p className="text-lg font-bold text-slate-100">{diasSemana[fechaObj.getDay()]} {fechaObj.getDate()}</p>
                  <p className="text-xs text-slate-500">{fechaObj.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}</p>
                  {fecha === hoyStr && <p className="text-[10px] text-cyan-400 font-medium">Hoy</p>}
                </div>
                <button onClick={() => cambiarDia(1)} className="text-slate-400 hover:text-slate-200" data-testid="fecha-next-btn">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              {/* Time slots */}
              <div className="grid grid-cols-3 gap-2">
                {slots.map(s => {
                  const isPast = s.razon === 'pasado';
                  const isCurrent = s.razon === 'en_curso';
                  const isFull = s.razon === 'lleno';
                  const isSelected = horaSeleccionada === s.hora;
                  return (
                    <button key={s.hora} onClick={() => s.disponible && setHoraSeleccionada(s.hora)}
                      disabled={!s.disponible}
                      className={`py-2.5 px-1 rounded-sm text-center transition-all flex flex-col items-center gap-0.5 ${
                        isSelected
                          ? 'bg-cyan-500 text-black font-bold ring-2 ring-cyan-400 ring-offset-1 ring-offset-slate-900'
                          : s.disponible
                            ? 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:border-cyan-500/30 border border-slate-700'
                            : isPast || isCurrent
                              ? 'bg-slate-800/20 text-slate-700 cursor-not-allowed border border-slate-800/30'
                              : 'bg-red-500/10 text-red-400/60 cursor-not-allowed border border-red-900/20'
                      }`}
                      data-testid={`slot-${s.hora}`}>
                      <span className="text-xs font-mono font-semibold">{s.hora}</span>
                      <span className={`text-[10px] leading-tight ${isSelected ? 'text-black/70' : ''}`}>
                        {isPast ? 'Pasado' :
                         isCurrent ? 'En curso' :
                         isFull ? 'Lleno' :
                         `${s.cupos} cupo${s.cupos !== 1 ? 's' : ''}`}
                      </span>
                    </button>
                  );
                })}
                {slots.length === 0 && (
                  <p className="col-span-3 text-sm text-slate-500 text-center py-4" data-testid="agenda-mensaje-sin-slots">
                    {mensajeSlots || 'No hay horarios disponibles para este dia'}
                  </p>
                )}
              </div>
              {/* Service selection */}
              {negocio.servicios.length > 0 && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Servicio</Label>
                  <Select value={servicioSeleccionado} onValueChange={setServicioSeleccionado}>
                    <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200 mt-1" data-testid="servicio-select">
                      <SelectValue placeholder="Seleccionar servicio..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {negocio.servicios.map(s => (
                        <SelectItem key={s.id} value={s.id} className="text-slate-300">
                          {s.nombre} - ${s.precio}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button onClick={() => {
                if (!horaSeleccionada) { toast.error('Selecciona un horario'); return; }
                setPaso(2);
              }} className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm"
                data-testid="siguiente-datos-btn">
                Siguiente
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Client data */}
        {paso === 2 && (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <button onClick={() => setPaso(1)} className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1">
                  <ChevronLeft className="w-3.5 h-3.5" /> Cambiar horario
                </button>
                <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-700 text-[10px]">
                  <Clock className="w-3 h-3 mr-1" />{fecha} - {horaSeleccionada}
                </Badge>
              </div>
              <h3 className="text-sm font-bold text-slate-300 flex items-center gap-2">
                <User className="w-4 h-4 text-cyan-400" /> Tus datos
              </h3>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Nombre *</Label>
                <Input value={form.cliente_nombre} onChange={e => setForm({...form, cliente_nombre: e.target.value})}
                  placeholder="Tu nombre completo" className="bg-slate-950 border-slate-700 text-slate-200 mt-1"
                  data-testid="agenda-nombre-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Telefono * (10 digitos)</Label>
                <Input value={form.cliente_telefono}
                  onChange={e => setForm({...form, cliente_telefono: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                  placeholder="10 digitos" inputMode="numeric" pattern="[0-9]*" maxLength={10}
                  className="bg-slate-950 border-slate-700 text-slate-200 mt-1 font-mono"
                  data-testid="agenda-telefono-input" />
                {form.cliente_telefono && form.cliente_telefono.length !== 10 && (
                  <p className="text-[10px] text-red-400 mt-1">Faltan {10 - form.cliente_telefono.length} digitos</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Placa *</Label>
                  <Input value={form.placa} onChange={e => setForm({...form, placa: e.target.value})}
                    placeholder="ABC-123" className="bg-slate-950 border-slate-700 text-slate-200 mt-1"
                    data-testid="agenda-placa-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Tipo vehiculo</Label>
                  <Select value={form.tipo_vehiculo} onValueChange={v => setForm({...form, tipo_vehiculo: v})}>
                    <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      <SelectItem value="auto" className="text-slate-300">Auto</SelectItem>
                      <SelectItem value="camioneta" className="text-slate-300">Camioneta</SelectItem>
                      <SelectItem value="moto" className="text-slate-300">Moto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Notas (opcional)</Label>
                <Input value={form.notas} onChange={e => setForm({...form, notas: e.target.value})}
                  placeholder="Algo que debamos saber..." className="bg-slate-950 border-slate-700 text-slate-200 mt-1" />
              </div>
              <Button onClick={handleAgendar} disabled={enviando}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm"
                data-testid="confirmar-cita-btn">
                {enviando ? 'Agendando...' : 'Confirmar Cita'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
